import { ArrowLeft, Copy, Download, Loader2 } from "lucide-react"
import { ScaleSheetDialog } from "@/components/scale-sheet/scale-sheet-dialog"
import { ScaleSheetEditPanel } from "@/components/scale-sheet/scale-sheet-edit-panel"
import { ScaleSheetPreview } from "@/components/scale-sheet/scale-sheet-preview"
import {
  useEntityScaleSheetModal,
  type EntityScaleSheetHeightUpdate,
  type EntityScaleSheetLoadResult,
  type EntityScaleSheetModalLabels,
  type EntityScaleSheetModalProps,
  type EntityScaleSheetRow,
} from "@/components/scale-sheet/use-entity-scale-sheet-modal"
import { Button } from "@/components/ui/primitives"
import { ImageResolutionLabel } from "@/components/ui/image-resolution-label"
import { CANVAS_WIDTH } from "@/lib/scale-sheet-layout"

export type {
  EntityScaleSheetHeightUpdate,
  EntityScaleSheetLoadResult,
  EntityScaleSheetModalLabels,
  EntityScaleSheetModalProps,
  EntityScaleSheetRow,
}

export function EntityScaleSheetModal(props: EntityScaleSheetModalProps) {
  const {
    open,
    nested,
    isShot,
    labels,
    t,
    error,
    loading,
    data,
    characters,
    minEntities,
    isViewMode,
    dirtyCount,
    applying,
    copying,
    previewContentWidth,
    previewLoading,
    serverImageOpenUrl,
    enterEditMode,
    showServerPreview,
    previewFigures,
    layout,
    serverImageReady,
    heightCmForEntity,
    markHeightTouchedByKey,
    showFigureRegenerate,
    regenerateFigureByKey,
    regenerateFigureTitle,
    isFigureBusyByKey,
    figureGenerateDisabled,
    hasFigureBusyCallback,
    handleClose,
    handleApplyAll,
    exitEditMode,
    showBackInFooter,
    showScaleSheetResolution,
    imageNaturalSize,
    scaleSheetDownloadUrl,
    canDownloadScaleSheet,
    handleScaleSheetDownload,
    handleScaleSheetCopy,
  } = useEntityScaleSheetModal(props)

  const {
    missingFigurePlaceholder,
    minHeightCm,
    minDragHeightCm,
    minHeightCmForEntity,
    minDragHeightCmForEntity,
    showExportActions = true,
  } = props

  const scaleSheetHeaderActions = (
    <>
      {showExportActions && showScaleSheetResolution ? <ImageResolutionLabel size={imageNaturalSize} /> : null}
      {showExportActions && data?.image_url && scaleSheetDownloadUrl ? (
        <>
          <button
            type="button"
            onClick={handleScaleSheetDownload}
            disabled={!canDownloadScaleSheet}
            className="inline-flex items-center gap-1.5 rounded-lg border border-ui-border bg-ui-surface-raised px-2.5 py-1.5 text-xs text-ui-text-primary transition-colors hover:bg-ui-surface-raised disabled:opacity-40"
            title={t("common.downloadOriginal", { ns: "shot" })}
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">{t("common.downloadOriginal", { ns: "shot" })}</span>
          </button>
          <button
            type="button"
            onClick={() => void handleScaleSheetCopy()}
            disabled={!canDownloadScaleSheet || copying}
            className="inline-flex items-center gap-1.5 rounded-lg border border-ui-border bg-ui-surface-raised px-2.5 py-1.5 text-xs text-ui-text-primary transition-colors hover:bg-ui-surface-raised disabled:opacity-40"
            title={t("common.copy", { ns: "shot" })}
          >
            {copying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />}
            <span className="hidden sm:inline">{t("common.copy", { ns: "shot" })}</span>
          </button>
        </>
      ) : null}
    </>
  )

  const backButton = showBackInFooter ? (
    <Button type="button" size="sm" variant="ghost" className="gap-1.5" onClick={exitEditMode}>
      <ArrowLeft className="h-3.5 w-3.5" />
      {t("back", { ns: "common" })}
    </Button>
  ) : null

  const footer =
    isViewMode && dirtyCount === 0 ? (
      <div className="flex justify-end">
        <Button type="button" size="sm" variant="ghost" onClick={() => handleClose(false)}>
          {labels.close}
        </Button>
      </div>
    ) : (
      <div className="flex flex-wrap items-center justify-between gap-3">
        {backButton ?? <span />}
        <div className="flex flex-wrap items-center justify-end gap-2">
          {dirtyCount > 0 ? (
            <Button type="button" size="sm" disabled={applying} onClick={() => void handleApplyAll()}>
              {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              {labels.applyAll}
            </Button>
          ) : null}
          <Button type="button" size="sm" variant="ghost" onClick={() => handleClose(false)}>
            {labels.close}
          </Button>
        </div>
      </div>
    )

  return (
    <ScaleSheetDialog
      open={open}
      onOpenChange={handleClose}
      title={isShot ? labels.previewTitle : labels.title}
      description={isShot ? undefined : labels.hint}
      footer={footer}
      headerActions={scaleSheetHeaderActions}
      nested={nested}
    >
      {error ? <p className="text-ui-status-error text-sm">{error}</p> : null}
      {loading && !data ? (
        <ScaleSheetPreview
          figures={[]}
          contentWidth={CANVAS_WIDTH}
          loading
          placeholderSurface="sunken"
          missingFigurePlaceholder={missingFigurePlaceholder}
        />
      ) : characters.length < minEntities ? (
        error ? null : (
          <p className="text-sm text-ui-text-secondary">{labels.needTwo}</p>
        )
      ) : isViewMode ? (
        showServerPreview ? (
          <ScaleSheetPreview
            figures={[]}
            contentWidth={previewContentWidth}
            serverImageUrl={data?.image_url}
            showServerImage={Boolean(data?.image_url)}
            loading={previewLoading}
            compactServerImage={Boolean(data?.image_url)}
            serverImageOpenUrl={serverImageOpenUrl}
            placeholderSurface="sunken"
            missingFigurePlaceholder={missingFigurePlaceholder}
            onServerImageClick={isShot ? enterEditMode : undefined}
          />
        ) : null
      ) : (
        <ScaleSheetEditPanel
          previewFigures={previewFigures}
          contentWidth={layout.content_width_preview}
          serverImageUrl={data?.image_url}
          showServerImage={serverImageReady}
          serverImageOpenUrl={serverImageOpenUrl}
          previewLoading={previewLoading}
          previewDisplayZone={serverImageReady ? undefined : layout.preview_display_zone}
          heightCmForEntity={heightCmForEntity}
          onHeightChange={markHeightTouchedByKey}
          missingFigurePlaceholder={missingFigurePlaceholder}
          minHeightCm={minHeightCm}
          minDragHeightCm={minDragHeightCm}
          minHeightCmForEntity={minHeightCmForEntity}
          minDragHeightCmForEntity={minDragHeightCmForEntity}
          heightLabel={labels.heightLabel}
          onRegenerateFigure={showFigureRegenerate ? regenerateFigureByKey : undefined}
          regenerateFigureTitle={regenerateFigureTitle}
          isFigureBusy={hasFigureBusyCallback ? isFigureBusyByKey : undefined}
          figureGenerateDisabled={figureGenerateDisabled}
        />
      )}
    </ScaleSheetDialog>
  )
}
